import { useEffect, useMemo, useState } from "react";

import { serverNow } from "@storyboard/web-client";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import { armExpiryTimer, computeTaskFold, NO_TASKS, type TaskFold, taskCollections } from "./tasks";

/** The server's clock as the page estimates it, as epoch milliseconds: the clock a task's expiry was stamped on. */
const serverClock = () => serverNow().getTime();

/**
 * The workspace's task fold, recomputed as tasks, pins and their acknowledgements land, and at each expiry.
 *
 * {@link NO_TASKS} while `enabled` is false, so a workspace without tasks never walks the collections. The fold reads
 * the server's clock as the page estimates it, the clock the dialog stamps an expiry on, so a browser whose clock is
 * off does not expire a task early or late. A timer set for the soonest expiry among the open tasks folds again the
 * moment it passes, so an expiring task leaves on time rather than at the next minute tick; each fold sets the timer
 * again.
 *
 * @param workspaceId - The workspace whose tasks are folded.
 * @param now - The page's minute clock, whose tick folds again so the lookback moves.
 * @param enabled - Whether the workspace's task surface is on.
 */
export function useTaskFold(workspaceId: string, now: number, enabled: boolean): TaskFold {
  // The clock the last expiry timer fired at; each change, like each minute tick, reads the server's clock again.
  const [expiryClock, setExpiryClock] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a tick or a fired timer is the trigger, not an input
  const at = useMemo(() => Math.max(serverClock(), expiryClock), [now, expiryClock]);
  const fold = useCollectionsCompute(
    taskCollections,
    () => (enabled ? computeTaskFold(workspaceId, at) : NO_TASKS),
    [enabled, workspaceId, at],
    "Dispatch:tasks",
  );
  useEffect(() => armExpiryTimer(fold, setExpiryClock, serverClock), [fold]);
  return fold;
}
